import { useMemo } from 'react';
import dayjs from 'dayjs';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  VersionDrawer,
  type VersionEntry,
} from '@shared/components/VersionDrawer';
import { sssApi } from '@api/sss';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { recordAuditAction } from '@features/audit/recordAction';

interface Props {
  open: boolean;
  faqId: string;
  currentAnswer: string;
  onClose: () => void;
}

export function SssVersionsDrawer({
  open,
  faqId,
  currentAnswer,
  onClose,
}: Props) {
  const { t } = useTranslation(['sss', 'common', 'validation', 'errors']);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ['sss-versions', faqId],
    queryFn: () => sssApi.listVersions(faqId),
    enabled: open,
  });

  const restoreMutation = useMutation({
    mutationFn: (versionNumber: number) => sssApi.restoreVersion(faqId, versionNumber),
    onSuccess: async (updated, versionNumber) => {
      toast.success(t('toast.versionRestored', { version: versionNumber }));
      await qc.invalidateQueries({ queryKey: ['sss', faqId] });
      await qc.invalidateQueries({ queryKey: ['sss-versions', faqId] });
      await qc.invalidateQueries({ queryKey: ['sss'] });
      void recordAuditAction({
        action: 'sss.version.restore',
        resource: 'sss',
        resourceId: updated.id,
        summary: `${updated.question} v${versionNumber} geri yüklendi`,
        meta: { versionNumber },
      });
      onClose();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const versions = useMemo<VersionEntry[]>(() => {
    const list = (data?.items ?? [])
      .slice()
      .sort((a, b) => b.versionNumber - a.versionNumber);
    return list.map((v, idx) => ({
      // We carry the version NUMBER in `id` because that's what the backend
      // restore endpoint expects (`/versions/{n}/restore`).
      id: String(v.versionNumber),
      label: t('versions.label', { version: v.versionNumber, note: v.note ?? t('versions.labelFallback') }),
      author: v.authorName,
      createdAt: dayjs(v.createdAt).format('DD.MM.YYYY HH:mm'),
      snapshot: v.snapshot.answer,
      current: idx === 0,
    }));
  }, [data, t]);

  return (
    <VersionDrawer
      open={open}
      onClose={onClose}
      versions={versions}
      currentContent={currentAnswer}
      onRestore={async (v) => {
        const n = Number(v.id);
        if (!Number.isFinite(n) || n < 1) return;
        await restoreMutation.mutateAsync(n);
      }}
    />
  );
}
